import { atom } from "jotai";
import { P, match } from "ts-pattern";

import type { Category, OnChooserCallback } from "../types";
import { categoriesAtom, selectCategoryAtom } from "./category";
import {
  contactsAtom,
  pendingScrollToContactIdAtom,
  selectContactAtom,
} from "./contact";
import { addressBookAtom, initialState } from "./state";

/**
 * Public API atoms mirror the AddressBookHandle interface.
 */

export const resetAddressBookAtom = atom(null, (_get, set) => {
  set(addressBookAtom, initialState);
});

export const setAddressBookNameAtom = atom(null, (_get, set, name: string) => {
  set(addressBookAtom, (draft) => {
    draft.addressBookName = name;
  });
});

export const setOnChooserCallbackAtom = atom(
  null,
  (_get, set, callback: OnChooserCallback) => {
    set(addressBookAtom, (draft) => {
      draft.onChooserCallback = callback;
    });
  },
);

export const setSelectedContactWithIdAtom = atom(
  null,
  async (get, set, id: number) => {
    // Try to find the contact in the currently loaded list
    let contact = get(contactsAtom).find((c) => c.id === id);

    // If not found, load its category first then search again
    if (!contact) {
      // Use the contact's own category data if available in any loaded list,
      // otherwise fall back to the "all" category
      const allCategory = get(categoriesAtom).find((c) => c.type === "all");

      if (allCategory) {
        await (set(
          selectCategoryAtom,
          allCategory,
        ) as unknown as Promise<void>);
        contact = get(contactsAtom).find((c) => c.id === id);
      }
    }

    if (contact) {
      // If the contact has categories, select the first one
      const contactCategory = contact.categories?.[0];

      if (contactCategory) {
        const category = get(categoriesAtom).find(
          (c) => c.key === contactCategory.key,
        );

        const currentCategory = get(addressBookAtom).selectedCategory;

        if (category && currentCategory?.key !== category.key) {
          await (set(selectCategoryAtom, category) as unknown as Promise<void>);
        }
      }

      set(pendingScrollToContactIdAtom, id);
      await (set(selectContactAtom, contact) as unknown as Promise<void>);
    }
  },
);

export const setSelectedCategoryWithIndexAtom = atom(
  null,
  async (get, set, index: number) => {
    const category = get(categoriesAtom)[index];

    if (category) {
      await (set(selectCategoryAtom, category) as unknown as Promise<void>);
    }
  },
);

export const setSelectedCategoryAtom = atom(
  null,
  async (get, set, type: Category["type"], key?: string) => {
    const category = match(type)
      .with("vcard", () => get(categoriesAtom).find((c) => c.key === key))
      .with(P.not("vcard"), () =>
        get(categoriesAtom).find((c) => c.type === type),
      )
      .otherwise(() => {
        console.warn("Invalid category input:", { type, key });
        return null;
      });

    if (category) {
      await (set(selectCategoryAtom, category) as unknown as Promise<void>);
    }
  },
);
